import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { requisitarAtivacao, useAcaoAtivacao } from '@/hooks/useAtivacaoLinhas';

interface Guia { topico: string; titulo: string; texto_template: string; url_visual: string | null }

// O manual que sai para o cliente é o MESMO do cadastro de guias que a IsabellA
// usa. Não existe segunda versão do texto: quem edita o guia edita os dois.
const PRINCIPAL: Record<string, string> = { esim_real: 'esim', esim_simcard: 'esim_para_chip_fisico' };

export function GuiasAtivacao({ pedidoId, linhaId, conversaId, tipo }: {
  pedidoId: string; linhaId?: string; conversaId?: string; tipo?: string | null;
}) {
  const [enviado, setEnviado] = useState('');
  const [erro, setErro] = useState('');
  const acao = useAcaoAtivacao(pedidoId);
  const dados = useQuery({ queryKey: ['ativacao-guias', pedidoId], enabled: !!pedidoId,
    queryFn: () => requisitarAtivacao<Guia[]>(`pedidos/${pedidoId}/guias`) });
  const destaque = tipo ? PRINCIPAL[tipo] : undefined;
  const guias = dados.data || [];
  const ordenados = destaque ? [...guias].sort((a, b) => Number(b.topico === destaque) - Number(a.topico === destaque)) : guias;

  async function enviar(g: Guia) {
    if (!linhaId || !conversaId) return;
    setErro(''); setEnviado('');
    try {
      await acao.mutateAsync({ path: `linhas/${linhaId}/enviar-guia`, body: { conversaId, topico: g.topico } });
      setEnviado(g.topico);
    } catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível enviar o manual.'); }
  }

  // Recolhido (Douglas 23/09/2026): aberto, o manual ocupava o modal inteiro e
  // empurrava a foto e o ICCID para baixo. Abre com o manual principal do tipo.
  return <details className="group space-y-2 border-t pt-2 text-xs">
    <summary className="cursor-pointer list-none font-medium text-muted-foreground hover:text-primary">Manuais de ativação e Minha Claro</summary>
    <p className="text-[10px] text-muted-foreground">O mesmo texto do cadastro de guias do CRM. Confira os dados do contrato antes de orientar. Não solicite senhas.</p>
    {erro && <p role="alert" className="text-[11px] text-destructive">{erro}</p>}
    {dados.isPending ? <p className="text-muted-foreground">Carregando manuais…</p>
      : dados.error ? <p role="alert" className="text-destructive">{dados.error.message}</p>
      : ordenados.map(g => <details key={g.topico} className="rounded border px-2 py-1" open={g.topico === destaque}>
        <summary className="cursor-pointer py-0.5">{g.titulo}</summary>
        <p className="max-h-52 overflow-y-auto whitespace-pre-wrap py-2 text-[11px]">{g.texto_template}</p>
        <div className="flex flex-wrap items-center gap-2 pb-1">
          {linhaId && conversaId && <Button size="sm" variant="outline" className="h-[29px] gap-1.5 px-2 text-[11px]"
            disabled={acao.isPending} onClick={() => void enviar(g)}>
            {acao.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Enviar ao cliente
          </Button>}
          {g.url_visual?.startsWith('https://') && <a className="text-primary underline" href={g.url_visual} target="_blank" rel="noopener noreferrer">Abrir manual ilustrado</a>}
          {enviado === g.topico && <span className="text-[10px] text-primary">Enviado na conversa.</span>}
        </div>
      </details>)}
    {!linhaId || !conversaId ? <p className="text-[10px] text-muted-foreground">Selecione a linha numa conversa vinculada para enviar o manual ao cliente.</p> : null}
  </details>;
}
